import OneSignal from 'react-onesignal';

// OneSignal configuration
const ONESIGNAL_APP_ID = process.env.REACT_APP_ONESIGNAL_APP_ID || '';

export const initializePushNotifications = async (config) => {
  if (!ONESIGNAL_APP_ID) {
    console.warn('OneSignal App ID not configured');
    return;
  }

  try {
    // Initialize OneSignal (Web SDK v16 via react-onesignal v3)
    await OneSignal.init({
      appId: ONESIGNAL_APP_ID,
      allowLocalhostAsSecureOrigin: true,
      serviceWorkerParam: { scope: '/' },
      notifyButton: {
        enable: false, // We use our own custom UI
      },
    });

    // Tag user with center_id for per-center notification targeting
    if (config?.center_id) {
      try {
        OneSignal.login(config.center_id);
      } catch {
        // login may not be available in all versions — fallback to tags
        try {
          OneSignal.User?.addTag?.('center_id', config.center_id);
        } catch { /* ignore */ }
      }
    }

    // Tag with center name for readable segmentation
    if (config?.name) {
      try {
        OneSignal.User?.addTag?.('center_name', config.name);
      } catch { /* ignore */ }
    }

    console.log('[PushNotifications] Initialized successfully');
  } catch (error) {
    console.error('[PushNotifications] Error initializing:', error);
  }
};

// Check if notifications are supported
export const isPushNotificationSupported = () => {
  return 'Notification' in window && 'serviceWorker' in navigator;
};

// Check notification permission status
export const getNotificationPermission = () => {
  if (!isPushNotificationSupported()) return 'unsupported';
  return Notification.permission; // 'default', 'granted', 'denied'
};

// Request notification permission via OneSignal's prompt
export const requestNotificationPermission = async () => {
  if (!isPushNotificationSupported()) return false;

  try {
    // v16 API: request permission via the Slidedown or native prompt
    if (OneSignal.Slidedown?.promptPush) {
      await OneSignal.Slidedown.promptPush();
    } else if (OneSignal.showSlidedownPrompt) {
      await OneSignal.showSlidedownPrompt();
    } else {
      // Fallback to native browser prompt
      const result = await Notification.requestPermission();
      return result === 'granted';
    }
    return true;
  } catch (error) {
    console.error('[PushNotifications] Error requesting permission:', error);
    return false;
  }
};

// Check if user is subscribed
export const isUserSubscribed = async () => {
  try {
    // v16 API
    if (OneSignal.User?.PushSubscription?.optedIn !== undefined) {
      return OneSignal.User.PushSubscription.optedIn;
    }
    // Fallback
    if (OneSignal.isPushNotificationsEnabled) {
      return await OneSignal.isPushNotificationsEnabled();
    }
    return Notification.permission === 'granted';
  } catch {
    return false;
  }
};

// Get user's OneSignal subscription ID
export const getUserId = async () => {
  try {
    // v16 API
    if (OneSignal.User?.PushSubscription?.id) {
      return OneSignal.User.PushSubscription.id;
    }
    // Fallback
    if (OneSignal.getUserId) {
      return await OneSignal.getUserId();
    }
    return null;
  } catch {
    return null;
  }
};

// Tag user for segmentation (e.g., center_id, preferences)
export const tagUser = async (key, value) => {
  try {
    if (OneSignal.User?.addTag) {
      OneSignal.User.addTag(key, value);
    } else if (OneSignal.sendTag) {
      await OneSignal.sendTag(key, value);
    }
  } catch (error) {
    console.error('[PushNotifications] Error tagging user:', error);
  }
};
